/**
 * hitomi.la `ComicSource` implementation.
 *
 * Auto-registered: the registry's `import.meta.glob('./*\/index.ts')` picks
 * up this file because the folder name is `hitomi` (no leading underscore).
 *
 * No login, no native favorites. Capabilities mark `favorites: true` because
 * the unified library view synthesizes a local-only favorites list from
 * IndexedDB for non-auth sources — see `src/features/library/` and the
 * "Local-only favorites" note in this module's README.
 *
 * The `listFavorites`/`addFavorite`/`removeFavorite` `ComicSource` methods
 * are deliberately NOT implemented here. The unified library hook detects
 * `capabilities.login === false` and reads from `db/favorites.ts` directly
 * instead of trying to delegate through the source.
 */

import type {
  ComicCard,
  ComicDetails,
  ComicSource,
  Page,
  PageRef,
  PopularOpts,
  SearchFiltersSchema,
  SearchQuery,
  SourceAuth,
} from '../types';

import {
  buildPageRefs,
  getGallery,
  listLatest,
  listPopular as apiListPopular,
  parseTagSpec,
  resolvePageRefUrl,
  searchByQuery,
  searchByTag,
} from './api';
import type { PopularRange } from './types';

const POPULAR_RANGES: PopularRange[] = ['today', 'week', 'month', 'year'];

function isPopularRange(value: string | undefined): value is PopularRange {
  return value !== undefined && (POPULAR_RANGES as string[]).includes(value);
}

const HITOMI_SEARCH_FILTERS: SearchFiltersSchema = {
  fields: [
    {
      id: 'sort',
      type: 'select',
      label: 'Sort',
      options: [
        { value: 'popular', label: 'Popular' },
        { value: 'date', label: 'Date added' },
      ],
    },
    {
      id: 'language',
      type: 'chips',
      label: 'Language',
      options: [
        { value: 'all', label: 'All' },
        { value: 'english', label: 'English' },
        { value: 'japanese', label: 'Japanese' },
        { value: 'chinese', label: 'Chinese' },
        { value: 'korean', label: 'Korean' },
      ],
    },
    {
      id: 'includeTags',
      type: 'tags',
      label: 'Tag (single)',
      placeholder: 'female:big_breasts',
    },
  ],
};

/**
 * Translate the 1-indexed `Page<T>.page` convention used across the SPA
 * into the 0-indexed page used by
 * hitomi's internal `.nozomi` Range math. We restamp the response's `page`
 * back to the external 1-indexed value so the paginator's `nextPageNumber`
 * keeps incrementing the same number it asked for.
 */
function withExternalPage(externalPage: number) {
  const internal = Math.max(0, externalPage - 1);
  return {
    internal,
    /** Wrap a hitomi-internal Page result back into the external page space. */
    wrap<T>(page: Page<T>): Page<T> {
      return { ...page, page: externalPage };
    },
  };
}

async function search(q: SearchQuery, _auth?: SourceAuth): Promise<Page<ComicCard>> {
  const language = q.language ?? 'all';
  const externalPage = Math.max(1, q.page ?? 1);
  const { internal: page, wrap } = withExternalPage(externalPage);
  const sort = q.sort === 'date' ? 'date' : 'popular';
  const orderBy = sort === 'date' ? '' : 'popular/week';
  const uploader = q.uploader?.trim();

  if ((!q.q || !q.q.trim()) && uploader && (!q.includeTags || q.includeTags.length === 0)) {
    return wrap(
      await searchByTag({ namespace: 'artist', name: uploader }, { page, language, orderBy }),
    );
  }

  // Tag-only query: use the matching `.nozomi` file directly. We only honor
  // the FIRST include tag here — hitomi's tag-intersection mode requires
  // pulling each nozomi in full and intersecting client-side, which is too
  // expensive for default UI. Phase 3 can layer that on top via includeTags
  // post-filtering if needed.
  if ((!q.q || !q.q.trim()) && q.includeTags && q.includeTags.length > 0) {
    const first = q.includeTags[0];
    if (!first) return { items: [], page: externalPage, hasMore: false };
    return wrap(await searchByTag(parseTagSpec(first), { page, language, orderBy }));
  }

  // Free-form keyword search: B-tree intersection of all whitespace-split
  // terms. We pass q.q directly; `searchByQuery` handles the normalization.
  if (q.q && q.q.trim()) {
    return wrap(await searchByQuery(q.q, { page, language }));
  }

  // Empty query: behave like the front page.
  if (sort === 'date') {
    return wrap(await listLatest({ page, language }));
  }
  return wrap(await apiListPopular({ timeframe: 'week', page, language }));
}

async function getPopular(opts?: PopularOpts, _auth?: SourceAuth): Promise<Page<ComicCard>> {
  const range = opts?.range;
  const timeframe: PopularRange = isPopularRange(range) ? range : 'week';
  const externalPage = Math.max(1, opts?.page ?? 1);
  const { internal: page, wrap } = withExternalPage(externalPage);
  return wrap(await apiListPopular({ timeframe, page }));
}

async function getComic(id: string, _auth?: SourceAuth): Promise<ComicDetails> {
  return getGallery(id);
}

async function getPages(id: string, _auth?: SourceAuth): Promise<PageRef[]> {
  return buildPageRefs(id);
}

async function resolveImageUrl(ref: PageRef, _auth?: SourceAuth): Promise<{ url: string }> {
  const url = await resolvePageRefUrl(ref);
  return { url };
}

/**
 * hitomi has no login concept. We expose a no-op `validateAuth` so the
 * Connect Account screen can render a friendly "no login required" state
 * uniformly via the same code path used by the other sources.
 */
async function validateAuth(_auth: SourceAuth): Promise<{ ok: boolean; username?: string }> {
  return { ok: true };
}

// Local-only favorites — see `src/features/library/` and this module's
// README. We deliberately do NOT implement `listFavorites`/`addFavorite`/
// `removeFavorite` on the source. The unified library hook reads from
// `db/favorites.ts` directly when `capabilities.login === false`, and a
// generic UI affordance ("Add to favorites") writes back to that store
// without going through any source-specific code.

const source: ComicSource = {
  id: 'hitomi',
  name: 'hitomi.la',
  description:
    'No login required. Static index files + a small JS resolver for image URLs.',
  capabilities: {
    login: false,
    favorites: true,
    multiFavCategories: false,
    search: true,
    advancedSearch: false,
    uploaderSearch: true,
    sortOptions: ['popular', 'date'],
    popularRanges: [...POPULAR_RANGES],
    loginNotes:
      'No login required. Browse, search, and read all work anonymously. Your favorites are stored locally on this device.',
  },
  searchFilters: HITOMI_SEARCH_FILTERS,
  search,
  getPopular,
  getComic,
  getPages,
  resolveImageUrl,
  validateAuth,
};

export default source;
